import {
  friendlySampleSubType,
  friendlySampleType,
  getParentType,
  SampleSubType,
  SampleType,
  sampleTypeByName,
  zSampleSubType,
  zSampleType,
} from '@resistapp/common/sampling-point-types';
import { zBioRep, zCoordinates } from '@resistapp/common/types';
import { groupBy } from 'lodash';
import { z } from 'zod';

export const zSampleSpecRow = z.object({
  sampleId: z.number(),
  sampleNumber: z.number(),
  bioReplica: zBioRep,
  sampleDescription: z.string(),
  sampleType: zSampleType,
  detailedSampleType: zSampleSubType,
  flowRate: z.number().nullable(),
  operatedFlowRate: z.number().nullable(),
  suspendedSolids: z.number().nullable(),
  bod: z.number().nullable(),
  filteredVolume: z.number().nullable(),
  primElutedDnaVolume: z.number().nullable(),
  primElutedDnaConcentration: z.number().nullable(),
  primElutedDnaQuality280: z.number().nullable(),
  primElutedDnaQuality230: z.number().nullable(),
  primDilutedDnaConcentration: z.number().nullable(),
  primDilutedDnaQuality280: z.number().nullable(),
  primDilutedDnaQuality230: z.number().nullable(),
  primDilutedDnaVolume: z.number().nullable(),
  primDnaExtractionKit: z.string().nullable(),
  sampleCollectionDate: z.date().nullable(),
  city: z.string().nullable(),
  stateOrRegion: z.string().nullable(),
  country: z.string(),
  latitudeLongitude: zCoordinates.nullable(),
});

export type SampleSpecRow = z.infer<typeof zSampleSpecRow>;

export const optionalSampleSpecColumns = [
  'flowRate',
  'operatedFlowRate',
  'suspendedSolids',
  'bod',
  'filteredVolume',
  'primDnaExtractionKit',
  'primElutedDnaVolume',
  'primElutedDnaConcentration',
  'primElutedDnaQuality280',
  'primElutedDnaQuality230',
  'primDilutedDnaConcentration',
  'primDilutedDnaQuality280',
  'primDilutedDnaQuality230',
  'primDilutedDnaVolume',
  // required either for all or expected for none
  'sampleCollectionDate',
  'stateOrRegion',
  'city',
  'country', // Added only to pass a few control samples in lab-workbook backfil
  'detailedSampleType', // Added only to pass https://docs.google.com/spreadsheets/d/1eJVsYZTdW7dYyY6SbpV4NbwsZRaja8Kg2uFEYeI3SC4/edit?gid=841317492#gid=841317492 in lab-workbook backfil
  'latitudeLongitude',
] as const satisfies ReadonlyArray<keyof SampleSpecRow>;

export const sampleSpecColumnKeys = Object.keys(zSampleSpecRow.shape) as Array<keyof SampleSpecRow>;

const legacySampleSpecColumnAliases = {
  dnaExtractionKit: 'primDnaExtractionKit',
  elutedDnaVolume: 'primElutedDnaVolume',
  elutedDnaConcentration: 'primElutedDnaConcentration',
  elutedDnaQuality280: 'primElutedDnaQuality280',
  elutedDnaQuality230: 'primElutedDnaQuality230',
  dilutedDnaConcentration: 'primDilutedDnaConcentration',
  dilutedDnaQuality280: 'primDilutedDnaQuality280',
  dilutedDnaQuality230: 'primDilutedDnaQuality230',
  dilutedDnaVolume: 'primDilutedDnaVolume',
} as const satisfies Record<string, keyof SampleSpecRow>;
const legacySampleSpecColumnAliasLookup: Record<string, keyof SampleSpecRow> = legacySampleSpecColumnAliases;

export function normalizeSampleSpecSpreadsheetColumns(columns: string[]): string[] {
  return columns
    .map(column => column.replace(/\*|,/g, ''))
    .map(column => column.replace(/260\/230/g, '230'))
    .map(column => column.replace(/260\/280/g, '280'))
    .map(column => column.split(/\(/)[0])
    .map(column => column.replace(/\s+/g, ' '))
    .map(column => column.trim())
    .map(column => column.toLowerCase())
    .map(column =>
      column
        .split(' ')
        .map((word, i) => (i > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(''),
    )
    .map(column => legacySampleSpecColumnAliasLookup[column] ?? column);
}

type Nullish = null | undefined;

type CoordinateTuple = [number, number];

const whitespacePattern = /[\s\u00A0]+/g;
const unavailableNumberTokens = new Set([
  'n/a',
  'na',
  'n.a.',
  'not available',
  'not applicable',
  'none',
  'unknown',
  '-',
]);
const coordinateSeparatorPattern = /[;,]/;
const yearFirstNumericPattern = /^(\d{4})([-/.])(\d{1,2})\2(\d{1,2})$/;
const fourDigitYearPattern = /^\d{4}$/;
const dayOfMonthPattern = /^\d{1,2}$/;
const ordinalSuffixPattern = /(st|nd|rd|th)$/i;
const textualDateOrders = [
  { yearIndex: 2, monthIndex: 1, dayIndex: 0 },
  { yearIndex: 2, monthIndex: 0, dayIndex: 1 },
  { yearIndex: 0, monthIndex: 1, dayIndex: 2 },
] as const;
const monthIndexByToken = new Map<string, number>([
  ['jan', 0],
  ['january', 0],
  ['feb', 1],
  ['february', 1],
  ['mar', 2],
  ['march', 2],
  ['apr', 3],
  ['april', 3],
  ['may', 4],
  ['jun', 5],
  ['june', 5],
  ['jul', 6],
  ['july', 6],
  ['aug', 7],
  ['august', 7],
  ['sep', 8],
  ['sept', 8],
  ['september', 8],
  ['oct', 9],
  ['october', 9],
  ['nov', 10],
  ['november', 10],
  ['dec', 11],
  ['december', 11],
]);
const environmentTypeValues = Object.values(SampleType);
const environmentSubTypeValues = Object.values(SampleSubType);
const groupedSubtypes = groupBy(environmentSubTypeValues, subtype => getParentType(subtype));
const environmentSubTypesByType = Object.fromEntries(
  environmentTypeValues.map(type => [type, groupedSubtypes[type] ?? []] as const),
) as Record<SampleType, SampleSubType[]>;

export const environmentTypeOptions = environmentTypeValues;
export const environmentSubTypeOptions = environmentSubTypeValues;

const environmentTypeByFriendlyLowerCase = Object.fromEntries(
  environmentTypeValues.map(type => [friendlySampleType(type).toLowerCase(), type]),
) as Record<string, SampleType>;
const environmentSubtypeByFriendlyLowerCase = Object.fromEntries(
  environmentSubTypeValues.map(subtype => [
    friendlySampleSubType(subtype, getParentType(subtype)).toLowerCase(),
    subtype,
  ]),
) as Record<string, SampleSubType>;

function normalizeNumberInput(value: unknown): number | Nullish | undefined {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(whitespacePattern, '').replace(',', '.');
    if (normalized === '') {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function normalizeTextInput(value: unknown): string | Nullish | undefined {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  return undefined;
}

function buildDateFromParts(parts: string[]): Date | undefined {
  if (parts.length !== 3) {
    return undefined;
  }
  const [yearStr, monthStr, dayStr] = parts;
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || !Number.isInteger(day)) {
    return undefined;
  }
  const candidate = new Date(year, monthIndex, day);
  if (
    Number.isNaN(candidate.getTime()) ||
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== monthIndex ||
    candidate.getDate() !== day
  ) {
    return undefined;
  }
  return candidate;
}

function normalizeDateInput(value: unknown): Date | Nullish | undefined {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    const candidate = parseSupportedDateFormats(trimmed);
    return candidate;
  }
  return undefined;
}

function parseSupportedDateFormats(value: string): Date | undefined {
  return parseYearFirstNumericFormat(value) ?? parseTextualMonthFormat(value);
}

function parseYearFirstNumericFormat(value: string): Date | undefined {
  const match = yearFirstNumericPattern.exec(value);
  if (!match) {
    return undefined;
  }
  const year = match[1];
  const month = match[3];
  const day = match[4];
  return buildDateFromParts([year, month.padStart(2, '0'), day.padStart(2, '0')]);
}

function parseTextualMonthFormat(value: string): Date | undefined {
  const sanitized = value.replace(/,/g, ' ').replace(/[-]/g, ' ').replace(/[/.]/g, ' ').replace(/\s+/g, ' ').trim();
  if (sanitized === '') {
    return undefined;
  }
  const rawTokens = sanitized.split(' ');
  if (rawTokens.length !== 3) {
    return undefined;
  }
  const tokens = rawTokens
    .map(token => token.replace(ordinalSuffixPattern, '').replace(/\.+$/, ''))
    .map(token => token.trim())
    .filter(token => token !== '');
  if (tokens.length !== 3) {
    return undefined;
  }
  const candidate = textualDateOrders
    .map(order => {
      const monthToken = tokens[order.monthIndex]?.toLowerCase();
      const monthIndex = monthToken ? monthIndexByToken.get(monthToken) : undefined;
      if (typeof monthIndex !== 'number') {
        return undefined;
      }
      const yearToken = tokens[order.yearIndex];
      if (!fourDigitYearPattern.test(yearToken)) {
        return undefined;
      }
      const dayToken = tokens[order.dayIndex];
      if (!dayOfMonthPattern.test(dayToken)) {
        return undefined;
      }
      return buildDateFromParts([yearToken, String(monthIndex + 1).padStart(2, '0'), dayToken.padStart(2, '0')]);
    })
    .find((date): date is Date => Boolean(date));
  return candidate;
}

function normalizeCoordinateTuple(value: CoordinateTuple): SampleSpecRow['latitudeLongitude'] | undefined {
  const [lat, lon] = value;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return undefined;
  }
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
    return undefined;
  }
  return { lat, lon };
}

function normalizeCoordinateInput(value: unknown): SampleSpecRow['latitudeLongitude'] | Nullish | undefined {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    const cleaned = value.replace(whitespacePattern, ' ').trim();
    if (cleaned === '') {
      return null;
    }
    const fragments = cleaned
      .split(coordinateSeparatorPattern)
      .flatMap(fragment => fragment.trim().split(' '))
      .map(fragment => fragment.trim())
      .filter(Boolean);
    if (fragments.length !== 2) {
      return undefined;
    }
    const lat = Number(fragments[0].replace(',', '.'));
    const lon = Number(fragments[1].replace(',', '.'));
    const tuple: CoordinateTuple = [lat, lon];
    return normalizeCoordinateTuple(tuple);
  }
  if (typeof value !== 'object') {
    return undefined;
  }
  const candidate = value as { lat?: unknown; lon?: unknown };
  const candidateLat = typeof candidate.lat === 'number' ? candidate.lat : undefined;
  const candidateLon = typeof candidate.lon === 'number' ? candidate.lon : undefined;
  if (candidateLat == null || candidateLon == null) {
    return undefined;
  }
  return normalizeCoordinateTuple([candidateLat, candidateLon]);
}

export const zSampleSheetNumberCell = z.preprocess(normalizeNumberInput, z.number().nullable());
export const zSampleSheetTextCell = z.preprocess(normalizeTextInput, z.string().nullable());
export const zSampleSheetDateCell = z.preprocess(normalizeDateInput, z.date().nullable());
export const zSampleSheetCoordinatesCell = z.preprocess(normalizeCoordinateInput, zCoordinates.nullable());

export type SampleSheetCoordinates = z.infer<typeof zSampleSheetCoordinatesCell>;

export function formatIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function parseNumberCell(value: unknown): number | null | undefined {
  const result = zSampleSheetNumberCell.safeParse(value);
  return result.success ? result.data : undefined;
}

export function isUnavailableSampleMetadataValue(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim().replace(whitespacePattern, ' ').toLowerCase();
  return unavailableNumberTokens.has(normalized);
}

export function parseSampleMetadataNumberCell(value: unknown): number | null | undefined {
  if (isUnavailableSampleMetadataValue(value)) {
    return null;
  }
  return parseNumberCell(value);
}

export function parseTextCell(value: unknown): string | null | undefined {
  const result = zSampleSheetTextCell.safeParse(value);
  return result.success ? result.data : undefined;
}

export function parseDateCell(value: unknown): Date | null | undefined {
  const result = zSampleSheetDateCell.safeParse(value);
  return result.success ? result.data : undefined;
}

export function parseDateCellToIsoString(value: unknown): string | null | undefined {
  const parsed = parseDateCell(value);
  if (parsed === undefined) {
    return undefined;
  }
  return parsed ? formatIsoDate(parsed) : null;
}

export function parseCoordinatesCell(value: unknown): SampleSpecRow['latitudeLongitude'] | null | undefined {
  const result = zSampleSheetCoordinatesCell.safeParse(value);
  return result.success ? result.data : undefined;
}

export function normalizeSampleType(value: unknown): SampleType | null | undefined {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  if (environmentTypeValues.includes(trimmed as SampleType)) {
    return trimmed as SampleType;
  }
  const upper = trimmed.toUpperCase();
  if (environmentTypeValues.includes(upper as SampleType)) {
    return upper as SampleType;
  }
  const friendly = sampleTypeByName[trimmed] ?? environmentTypeByFriendlyLowerCase[trimmed.toLowerCase()];
  return friendly;
}

export function toSampleType(value: unknown): SampleType | null {
  const normalized = normalizeSampleType(value);
  return typeof normalized === 'undefined' ? null : normalized;
}

export function formatSampleTypeLabel(value: unknown): string {
  const envType = toSampleType(value);
  return envType ? friendlySampleType(envType) : '';
}

export function normalizeSamplingPointSubType(
  value: unknown,
  sampleType: SampleType | null,
): SampleSubType | null | undefined {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const subtype = matchSubtypeByCodeOrFriendly(trimmed);
  if (!subtype) {
    return subtype;
  }
  if (!sampleType) {
    return subtype;
  }
  return getParentType(subtype) === sampleType ? subtype : undefined;
}

export function toSamplingPointSubType(value: unknown): SampleSubType | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  return matchSubtypeByCodeOrFriendly(trimmed) ?? null;
}

export function getSubtypeValuesForType(sampleType: SampleType | null): SampleSubType[] {
  if (!sampleType) {
    return environmentSubTypeValues;
  }
  return environmentSubTypesByType[sampleType];
}

export function formatSamplingPointSubTypeLabel(value: unknown, sampleType: SampleType | null): string {
  const subtype = toSamplingPointSubType(value);
  if (!subtype) {
    return '';
  }
  const typeForLabel = sampleType ?? getParentType(subtype);
  return friendlySampleSubType(subtype, typeForLabel);
}

function matchSubtypeByCodeOrFriendly(value: string): SampleSubType | undefined {
  if (environmentSubTypeValues.includes(value as SampleSubType)) {
    return value as SampleSubType;
  }
  const upper = value.toUpperCase();
  if (environmentSubTypeValues.includes(upper as SampleSubType)) {
    return upper as SampleSubType;
  }
  return environmentSubtypeByFriendlyLowerCase[value.toLowerCase()];
}

export * from './constraints';
export * from './metadata';
